import styled from '@emotion/styled'
import { FunctionComponent, memo, ReactNode, useMemo } from 'react'
import { Chart, ReactGoogleChartProps } from 'react-google-charts'
import { IconName } from '~/components/Icon'
import { Legend, LegendProps } from '~/components/Legend'
import { TimelineMetricProps } from '~/components/TimelineMetric'
import { TimelineMetrics } from '~/components/TimelineMetrics'
import { Widget, WidgetProps } from '~/components/Widget'
import { WidgetMessage } from '~/components/WidgetMessage'
import { WithClassName } from '~/types/utils'
import { ChartWrapper, ChartWrapperSmall, Legends } from './AnalyticsCharts'

const WidgetContent = styled.div`
  display: flex;
  flex-direction: column;
`
const TimelineMetricsWrapper = styled.div`
  margin-left: 50px;
`

const ChartContainer = styled.div`
  flex: 1 1 auto;
`

export type AnalyticsChartWidgetMessage = {
  message: ReactNode
  icon: IconName
}

type AnalyticsChartWidgetProps = WithClassName &
  WidgetProps & {
    metrics?: TimelineMetricProps[]
    metricMessage?: AnalyticsChartWidgetMessage
    chart: ReactGoogleChartProps
    legends: LegendProps[]
    smallChart?: boolean
    notApplicable?: ReactNode
  }

const LegendsRenderer = memo<{ legends: LegendProps[] }>(({ legends }) => <Legends>{legends.map((legend, index) => legend && <Legend key={index} {...legend} />)}</Legends>)

const ChartRenderer = memo<{
  chart: ReactGoogleChartProps
  Wrapper: React.ComponentType<any>
}>(({ chart, Wrapper }) => <Wrapper>{chart.data && <Chart width={'100%'} height={'100%'} {...chart} />}</Wrapper>)

const AnalyticsChartWidget: FunctionComponent<AnalyticsChartWidgetProps> = memo(({ metrics, chart, legends, metricMessage, smallChart, notApplicable, ...props }) => {
  const Wrapper = useMemo(() => (smallChart ? ChartWrapperSmall : ChartWrapper), [smallChart])
  const isEmpty = useMemo(() => props.empty || (!props.isLoading && !legends.length), [props.empty, props.isLoading, legends.length])
  const shouldShowMetrics = useMemo(() => !!metrics || !!metricMessage, [metrics, metricMessage])

  return (
    <Widget {...props} empty={isEmpty}>
      <WidgetContent>
        {shouldShowMetrics && (
          <TimelineMetricsWrapper>
            {metrics && <TimelineMetrics metrics={metrics} />}
            {metricMessage && <WidgetMessage {...metricMessage} />}
          </TimelineMetricsWrapper>
        )}
        {notApplicable ?? (
          <ChartContainer>
            <ChartRenderer chart={chart} Wrapper={Wrapper} />
            <LegendsRenderer legends={legends} />
          </ChartContainer>
        )}
      </WidgetContent>
    </Widget>
  )
})

export { AnalyticsChartWidget }
